import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useData } from "../data/DataContext"
import {
  buildRagIndex,
  getRagFor,
  ragLabel,
  reportingYearForDate,
  type Rag,
  type RagIndex,
} from "../components/ObjectiveCard"
import type { Objective, Person, ScopeView } from "../data/types"
import { peopleInScope, normalisePersonName } from "../data/types"

/* ---------- helpers ---------- */

/**
 * Shortened label for heatmap column headers. Known defaults get a curated
 * abbreviation; anything else falls back to the full name (the column CSS
 * wraps if it has to).
 */
const DEFAULT_CATEGORY_SHORT: Record<string, string> = {
  "Risk Register": "Risk",
  "Health & Safety": "H&S",
  "Finance": "Finance",
  "Resource": "Resource",
  "Departmental 1": "Dept 1",
  "Departmental 2": "Dept 2",
  "Departmental 3": "Dept 3",
  "Departmental 4": "Dept 4",
}
function shortLabel(cat: string): string {
  return DEFAULT_CATEGORY_SHORT[cat] ?? cat
}

/**
 * Calendar months (1-12) in financial-year order: Apr first, Mar last.
 * Used by the month-pill selector at the top of the dashboard so the row
 * reads chronologically through the FY rather than calendar-Jan-first.
 */
const FY_MONTHS_1_INDEXED: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const SHORT_MONTHS_UC = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
]

/** Two-digit calendar year for a 1-indexed month within an FY. */
function shortYearForMonth(month1to12: number, reportingYear: number): string {
  const cellYear = month1to12 >= 4 ? reportingYear : reportingYear + 1
  return String(cellYear % 100).padStart(2, "0")
}

/* ---------- risk score ---------- */

type RiskBand = "very-safe" | "safe" | "low" | "moderate" | "high"

export interface RiskResult {
  score: number
  band: RiskBand
  label: string
  counts: { G: number; A: number; R: number; U: number; gaps: number }
  personsCounted: number
}

/**
 * Weighted RAG Index — aggregate risk score for one or more people in a
 * specific calendar month.
 *
 * Each expected category contributes a penalty based on its state:
 *   G=0, A=1, R=3, Unset=1, Gap=1.5
 * where "Unset" means the objective exists but has no RAG for the given
 * month, and "Gap" means no objective is recorded in that category at all.
 * Score = points / (3 × total expected categories) so it's comparable across
 * people with different objective counts.
 *
 * Bands: 0 → Very safe · ≤0.10 → Safe · ≤0.30 → Low · ≤0.55 → Moderate · >0.55 → High
 *
 * Returns null when no person has any objective at all.
 */
function computeRisk(
  personNames: Iterable<string>,
  objIndex: Map<string, Map<string, Objective>>,
  ragIndex: RagIndex,
  categoryNames: string[],
  month: number,
): RiskResult | null {
  let G = 0, A = 0, R = 0, U = 0, gaps = 0
  let personsCounted = 0
  for (const name of personNames) {
    const objs = objIndex.get(name)
    if (!objs || objs.size === 0) continue
    personsCounted++
    for (const cat of categoryNames) {
      if (!objs.has(cat)) { gaps++; continue }
      const rag = getRagFor(name, cat, month, ragIndex)
      if (rag === "G") G++
      else if (rag === "A") A++
      else if (rag === "R") R++
      else U++
    }
  }
  if (personsCounted === 0) return null
  const expected = G + A + R + U + gaps
  const points = 3 * R + A + U + 1.5 * gaps
  const max = 3 * expected
  const score = max > 0 ? points / max : 0
  let band: RiskBand
  let label: string
  if (score === 0) { band = "very-safe"; label = "Very safe" }
  else if (score <= 0.1) { band = "safe"; label = "Safe" }
  else if (score <= 0.3) { band = "low"; label = "Low" }
  else if (score <= 0.55) { band = "moderate"; label = "Moderate" }
  else { band = "high"; label = "High" }
  return { score, band, label, counts: { G, A, R, U, gaps }, personsCounted }
}

const RISK_BAND_STYLE: Record<RiskBand, string> = {
  "very-safe": "bg-rag-green text-white",
  "safe":      "bg-rag-green-bg text-rag-green",
  "low":       "bg-[rgb(185_138_58/0.14)] text-brass",
  "moderate":  "bg-rag-amber-bg text-rag-amber-ink",
  "high":      "bg-rag-red-bg text-rag-red",
}

function RiskPill({ risk }: { risk: RiskResult | null }) {
  if (!risk) {
    return (
      <span
        className="inline-flex items-center rounded-full border border-dashed border-rule px-[8px] py-[2px] font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3"
        title="No objectives recorded"
      >
        —
      </span>
    )
  }
  const tooltip = `Weighted RAG · G ${risk.counts.G} · A ${risk.counts.A} · R ${risk.counts.R}${
    risk.counts.U ? ` · unset ${risk.counts.U}` : ""
  }${risk.counts.gaps ? ` · gaps ${risk.counts.gaps}` : ""} · score ${risk.score.toFixed(2)}`
  return (
    <span
      title={tooltip}
      className={[
        "inline-flex items-center justify-center rounded-full px-[9px] py-[2px] font-mono text-[10px] uppercase tracking-[0.08em] font-medium",
        RISK_BAND_STYLE[risk.band],
      ].join(" ")}
    >
      {risk.label}
    </span>
  )
}

/* ---------- page ---------- */

export function TeamDashboardPage() {
  const { people, objectives, ragScores, monthLocks, categories, departments, me, refresh } = useData()

  // Refresh data whenever the user navigates to /team (mount). `refresh` is
  // a quiet background fetch — it updates the heatmap in place when new
  // data arrives without flashing a fullscreen loader. Ensures the dashboard
  // always reflects the latest scores when you click it from the sidebar,
  // rather than relying on the cached context from initial app load.
  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When true, include Team="User" self-service people in the heatmap.
  // Default off — the board-facing view is Exec/CLT only.
  const [showUsers, setShowUsers] = useState(false)

  // Scope view. CLT defaults to "My Team" (self + same-dept + people covered);
  // Admin/Exec default to "Exec Overview" (all active Exec/CLT, with optional
  // Show Users). "People I Cover" is a third segment that shows self +
  // everyone in me.covers (any team, including Users) — a blank heatmap if
  // you cover nobody. The parallel toggle on /reports has all three segments
  // (plus its own Org Chart mode). Show Users is only meaningful in
  // execOverview — in myTeam and peopleICover, in-scope Users are always
  // included regardless of the checkbox.
  const [viewMode, setViewMode] = useState<ScopeView>(
    me.role === "CLT" ? "myTeam" : "execOverview",
  )

  // RAG filter — checked colours are visible. A person is hidden if they have
  // at least one scored cell AND every scored cell is in an unchecked colour.
  // People with no scored cells are always visible (nothing to filter on).
  const [ragFilter, setRagFilter] = useState({ R: true, A: true, G: true })

  // Selected calendar month (1-12) driving the heatmap, risk pills and RAG
  // filter visibility check. Defaults to today's calendar month so the
  // dashboard opens on "current state". The pill row at the top of the page
  // lets the user retro-view earlier or peek at later months in the FY.
  const [selectedMonth, setSelectedMonth] = useState<number>(() => new Date().getMonth() + 1)

  const categoryNames = useMemo(
    () => categories.map((c) => c.name),
    [categories],
  )
  const departmentNames = useMemo(
    () => departments.map((d) => d.name),
    [departments],
  )

  const reportingYear = reportingYearForDate()
  const ragIndex = useMemo(
    () => buildRagIndex(ragScores, reportingYear),
    [ragScores, reportingYear],
  )

  // The "root Exec" — the top of the org chart. Preferred signal: an Exec
  // who has other Execs reporting to them (same Level-0 detection used by
  // OrgChart.tsx). Fallback: an Exec whose CoveredBy doesn't resolve to
  // another active Exec (flat org case with multiple peers — pick the first).
  //
  // Used to float the CEO's department to the top of the dashboard, because
  // the board expects to see the CEO at the top of a board-facing view, not
  // buried at the bottom of the departmental taxonomy.
  const topExec = useMemo(() => {
    const norm = (s: string) => s.trim().toLowerCase()
    const execs = people.filter((p) => p.active && p.team === "Exec")
    const execNames = new Set(execs.map((e) => norm(e.fullName)))

    // Preferred: Exec with at least one other Exec reporting via CoveredBy
    for (const e of execs) {
      const eKey = norm(e.fullName)
      const hasExecReport = execs.some(
        (other) =>
          other !== e &&
          !!other.coveredBy &&
          norm(other.coveredBy) === eKey,
      )
      if (hasExecReport) return e
    }

    // Fallback: Exec whose CoveredBy doesn't resolve to another active Exec
    for (const e of execs) {
      if (!e.coveredBy) return e
      if (!execNames.has(norm(e.coveredBy))) return e
    }

    return null
  }, [people])

  // Build a map: personName -> category -> Objective (first match)
  const objIndex = useMemo(() => {
    const map = new Map<string, Map<string, Objective>>()
    for (const o of objectives) {
      if (o.status === "Deleted") continue
      if (!o.category) continue
      const inner = map.get(o.personName) ?? new Map<string, Objective>()
      if (!inner.has(o.category)) inner.set(o.category, o)
      map.set(o.personName, inner)
    }
    return map
  }, [objectives])

  // Group active people by department in the order defined by the Departments
  // taxonomy list. `peopleInScope` decides who's in scope given the current
  // view (myTeam vs execOverview) and the Show Users toggle (execOverview
  // only). Shared with /reports so the two surfaces stay in sync.
  const grouped = useMemo(() => {
    const scopedNames = peopleInScope(
      people,
      me.matchedPerson ?? null,
      me.covers,
      viewMode,
      showUsers,
    )
    const active = people.filter(
      (p) => p.active && scopedNames.has(normalisePersonName(p.fullName)),
    )
    const map = new Map<string, Person[]>()
    for (const p of active) {
      const key = p.department ?? "Other"
      const arr = map.get(key) ?? []
      arr.push(p)
      map.set(key, arr)
    }
    // Sort each dept's people: Exec first, then alphabetical
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.team !== b.team) {
          if (a.team === "Exec") return -1
          if (b.team === "Exec") return 1
        }
        return a.fullName.localeCompare(b.fullName)
      })
    }
    // Ordered: taxonomy departments in their insertion order, then any
    // department that people use but isn't in the taxonomy list, then "Other".
    const entries: Array<[string, Person[]]> = []
    const seen = new Set<string>()
    for (const d of departmentNames) {
      const arr = map.get(d)
      if (arr && arr.length) {
        entries.push([d, arr])
        seen.add(d)
      }
    }
    for (const [key, arr] of map.entries()) {
      if (key === "Other") continue
      if (seen.has(key)) continue
      entries.push([key, arr])
    }
    const other = map.get("Other")
    if (other && other.length) entries.push(["Other", other])

    // CEO promotion: only in execOverview. In myTeam the CEO may not even
    // be in scope, and the user generally wants their own department first.
    if (viewMode === "execOverview" && topExec && topExec.department) {
      const idx = entries.findIndex(([d]) => d === topExec.department)
      if (idx > 0) {
        const [ceoEntry] = entries.splice(idx, 1)
        entries.unshift(ceoEntry)
      }
    }

    return entries
  }, [people, showUsers, viewMode, me.matchedPerson, me.covers, departmentNames, topExec])

  // Risk rollups — computed over the unfiltered `grouped` so that RAG
  // filters don't distort the per-dept score (filtering to Red only would
  // otherwise make every dept look worse than it actually is). Rolls up
  // against `selectedMonth` so changing the month pill repaints them.
  const personRisks = useMemo(() => {
    const m = new Map<number, RiskResult | null>()
    for (const [, members] of grouped) {
      for (const p of members) {
        m.set(p.id, computeRisk([p.fullName], objIndex, ragIndex, categoryNames, selectedMonth))
      }
    }
    return m
  }, [grouped, objIndex, ragIndex, categoryNames, selectedMonth])

  const deptRisks = useMemo(() => {
    const m = new Map<string, RiskResult | null>()
    for (const [dept, members] of grouped) {
      m.set(
        dept,
        computeRisk(members.map((p) => p.fullName), objIndex, ragIndex, categoryNames, selectedMonth),
      )
    }
    return m
  }, [grouped, objIndex, ragIndex, categoryNames, selectedMonth])

  // Set of PersonIds whose (PersonId, reportingYear, selectedMonth) slot is
  // currently locked. Drives the read-only lock icon in PersonRow's name
  // cell. Recomputes when the user changes the month pill so the icon only
  // appears for the month being viewed - someone who's locked May but not
  // June will only show the icon when May is the selected pill.
  // Read-only display; nothing here mutates locks - that happens via the
  // pill on My Objectives (see queue item 4 Phase B / commit 41eac09).
  const lockedPersonIds = useMemo(() => {
    const s = new Set<number>()
    for (const l of monthLocks) {
      if (l.year === reportingYear && l.month === selectedMonth) {
        s.add(l.personId)
      }
    }
    return s
  }, [monthLocks, reportingYear, selectedMonth])

  const totalPeople = useMemo(
    () => grouped.reduce((s, [, arr]) => s + arr.length, 0),
    [grouped],
  )

  // Apply RAG filter on top of the stable grouping. Empty departments (all
  // their people filtered out) are dropped from the display. Filter visibility
  // checks the currently-selected month, so a person whose September is all
  // red but October is all green will be hidden in September with the Red
  // filter off but visible in October.
  const visibleGrouped = useMemo(() => {
    const allShown = ragFilter.R && ragFilter.A && ragFilter.G
    if (allShown) return grouped
    const shouldHide = (person: Person): boolean => {
      const objs = objIndex.get(person.fullName)
      if (!objs || objs.size === 0) return false
      let hasAnyScore = false
      for (const cat of objs.keys()) {
        const rag = getRagFor(person.fullName, cat, selectedMonth, ragIndex)
        if (rag == null) continue
        hasAnyScore = true
        if (rag === "R" && ragFilter.R) return false
        if (rag === "A" && ragFilter.A) return false
        if (rag === "G" && ragFilter.G) return false
      }
      return hasAnyScore
    }
    return grouped
      .map(([dept, members]) => [dept, members.filter((p) => !shouldHide(p))] as [string, Person[]])
      .filter(([, members]) => members.length > 0)
  }, [grouped, ragFilter, objIndex, ragIndex, selectedMonth])

  const visiblePeople = useMemo(
    () => visibleGrouped.reduce((s, [, arr]) => s + arr.length, 0),
    [visibleGrouped],
  )
  const hiddenByRag = totalPeople - visiblePeople

  // Real lookup, bound to the live ragIndex and the selected month.
  const getRag = (personName: string, category: string): Rag =>
    getRagFor(personName, category, selectedMonth, ragIndex)

  const myName = me.matchedPerson?.fullName ?? null

  const columnCount = categoryNames.length

  return (
    <div>
      {/* Month pill row — FY-ordered (Apr → Mar). Single-select; click any
          pill to repaint the heatmap, risk rollups and RAG-filter visibility
          for that month. Today's calendar month gets a brass ring even when
          unselected so users can always orient on "now". */}
      <div className="mb-5">
        <div className="mb-[8px] font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
          Reporting month
        </div>
        <div className="flex flex-wrap gap-[5px]">
          {FY_MONTHS_1_INDEXED.map((m) => {
            const isSelected = m === selectedMonth
            const isCurrentCal = m === ragIndex.currentMonth
            return (
              <button
                key={m}
                type="button"
                onClick={() => setSelectedMonth(m)}
                aria-pressed={isSelected}
                className={[
                  "rounded-sm border px-[10px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  isSelected
                    ? "border-nymr-navy bg-nymr-navy text-white"
                    : "border-rule-strong bg-card text-ink-2 hover:text-ink hover:border-rule-strong",
                  isCurrentCal && !isSelected ? "outline outline-2 outline-offset-1 outline-brass" : "",
                ].join(" ")}
                title={`${SHORT_MONTHS_UC[m - 1]} ${shortYearForMonth(m, reportingYear)}${
                  isCurrentCal ? " · current month" : ""
                }`}
              >
                {SHORT_MONTHS_UC[m - 1]} {shortYearForMonth(m, reportingYear)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Intro + controls */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-3xl text-[14px] leading-[1.6] text-ink-2">
          {visiblePeople} of {totalPeople} active people across{" "}
          {visibleGrouped.length} department
          {visibleGrouped.length === 1 ? "" : "s"}
          {hiddenByRag > 0 ? (
            <>
              {" "}
              <span className="text-ink-3">
                ({hiddenByRag} hidden by RAG filter)
              </span>
            </>
          ) : null}
          . Each cell is one objective; click any name to open that person's
          record. Cells with a dashed outline indicate a{" "}
          <span className="font-medium text-ink">gap</span> — no objective
          recorded in that category.
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Scope view toggle. My Team / People I Cover / Exec Overview.
              /reports has the same three segments plus an Org Chart option. */}
          <div className="inline-flex rounded-sm border border-rule-strong bg-card p-[2px]">
            <button
              type="button"
              onClick={() => setViewMode("myTeam")}
              className={[
                "rounded-sm px-[10px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                viewMode === "myTeam"
                  ? "bg-nymr-navy text-white"
                  : "text-ink-2 hover:text-ink",
              ].join(" ")}
            >
              My Team
            </button>
            <button
              type="button"
              onClick={() => setViewMode("peopleICover")}
              className={[
                "rounded-sm px-[10px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                viewMode === "peopleICover"
                  ? "bg-nymr-navy text-white"
                  : "text-ink-2 hover:text-ink",
              ].join(" ")}
            >
              People I Cover
            </button>
            <button
              type="button"
              onClick={() => setViewMode("execOverview")}
              className={[
                "rounded-sm px-[10px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                viewMode === "execOverview"
                  ? "bg-nymr-navy text-white"
                  : "text-ink-2 hover:text-ink",
              ].join(" ")}
            >
              Exec Overview
            </button>
          </div>
          <RagFilterChip
            label="Red"
            checked={ragFilter.R}
            swatch="bg-rag-red"
            onChange={(v) => setRagFilter((f) => ({ ...f, R: v }))}
          />
          <RagFilterChip
            label="Amber"
            checked={ragFilter.A}
            swatch="bg-rag-amber"
            onChange={(v) => setRagFilter((f) => ({ ...f, A: v }))}
          />
          <RagFilterChip
            label="Green"
            checked={ragFilter.G}
            swatch="bg-rag-green"
            onChange={(v) => setRagFilter((f) => ({ ...f, G: v }))}
          />
          {viewMode === "execOverview" ? (
            <label className="inline-flex cursor-pointer items-center gap-[8px] rounded-sm border border-rule-strong bg-card px-[12px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:text-ink">
              <input
                type="checkbox"
                checked={showUsers}
                onChange={(e) => setShowUsers(e.target.checked)}
                className="h-[14px] w-[14px] accent-nymr-navy"
              />
              Show Users
            </label>
          ) : null}
        </div>
      </div>

      {/* Heatmap */}
      <div className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
        <div
          className="grid text-[12.5px]"
          style={{ gridTemplateColumns: `220px repeat(${Math.max(1, columnCount)}, 1fr) 128px` }}
        >
          {/* Column header row */}
          <div className="flex items-center bg-paper-sunk px-[18px] py-[14px] font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3 border-b border-rule">
            Person &middot; Department
          </div>
          {categoryNames.map((cat) => (
            <div
              key={cat}
              title={cat}
              className="border-l border-rule border-b bg-paper-sunk px-[10px] py-[14px] text-center font-mono text-[10px] uppercase leading-[1.3] tracking-[0.08em] text-ink-2"
            >
              {shortLabel(cat)}
            </div>
          ))}
          <div
            title="Overall weighted risk score across all objectives"
            className="border-l border-rule border-b bg-paper-sunk px-[10px] py-[14px] text-center font-mono text-[10px] uppercase leading-[1.3] tracking-[0.08em] text-ink-2"
          >
            Risk
          </div>

          {/* Rows grouped by department */}
          {visibleGrouped.map(([dept, members]) => (
            <DepartmentBlock
              key={dept}
              dept={dept}
              members={members}
              objIndex={objIndex}
              getRag={getRag}
              myName={myName}
              categoryNames={categoryNames}
              deptRisk={deptRisks.get(dept) ?? null}
              personRisks={personRisks}
              lockedPersonIds={lockedPersonIds}
              selectedMonth={selectedMonth}
              reportingYear={reportingYear}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/* ---------- department block ---------- */

function DepartmentBlock({
  dept,
  members,
  objIndex,
  getRag,
  myName,
  categoryNames,
  deptRisk,
  personRisks,
  lockedPersonIds,
  selectedMonth,
  reportingYear,
}: {
  dept: string
  members: Person[]
  objIndex: Map<string, Map<string, Objective>>
  getRag: (personName: string, category: string) => Rag
  myName: string | null
  categoryNames: string[]
  deptRisk: RiskResult | null
  personRisks: Map<number, RiskResult | null>
  lockedPersonIds: Set<number>
  selectedMonth: number
  reportingYear: number
}) {
  return (
    <>
      {/* Department break row — dept name in the first column, then the
          category column headers repeated across the remaining columns so the
          reader doesn't have to scroll to the top to know what each cell is,
          and finally the dept-level risk rollup in the rightmost column. */}
      <div className="flex items-center gap-[10px] border-t border-rule bg-paper-sunk px-[18px] py-[10px] font-display text-[13px] font-medium text-ink-2">
        <span>{dept}</span>
        <span className="font-mono text-[10px] tracking-[0.08em] text-ink-3">
          {members.length} {members.length === 1 ? "person" : "people"}
        </span>
      </div>
      {categoryNames.map((cat) => (
        <div
          key={cat}
          title={cat}
          className="border-t border-l border-rule bg-paper-sunk px-[10px] py-[10px] text-center font-mono text-[10px] uppercase leading-[1.3] tracking-[0.08em] text-ink-3"
        >
          {shortLabel(cat)}
        </div>
      ))}
      <div
        title={deptRisk ? `${dept} · aggregate · ${deptRisk.label}` : `${dept} · no objectives`}
        className="flex items-center justify-center border-t border-l border-rule bg-paper-sunk px-[10px] py-[10px]"
      >
        <RiskPill risk={deptRisk} />
      </div>

      {/* Person rows */}
      {members.map((p) => {
        const personObjs = objIndex.get(p.fullName)
        const isSelf = myName !== null && p.fullName === myName
        const isLocked = lockedPersonIds.has(p.id)
        return (
          <PersonRow
            key={p.id}
            person={p}
            personObjs={personObjs}
            getRag={getRag}
            isSelf={isSelf}
            categoryNames={categoryNames}
            risk={personRisks.get(p.id) ?? null}
            isLocked={isLocked}
            selectedMonth={selectedMonth}
            reportingYear={reportingYear}
          />
        )
      })}
    </>
  )
}

/* ---------- person row ---------- */

function PersonRow({
  person,
  personObjs,
  getRag,
  isSelf,
  categoryNames,
  risk,
  isLocked,
  selectedMonth,
  reportingYear,
}: {
  person: Person
  personObjs: Map<string, Objective> | undefined
  getRag: (personName: string, category: string) => Rag
  isSelf: boolean
  categoryNames: string[]
  risk: RiskResult | null
  isLocked: boolean
  selectedMonth: number
  reportingYear: number
}) {
  const teamPill =
    person.team === "Exec"
      ? "bg-[#E8E1FC] text-[#4B3F90]"
      : person.team === "CLT"
        ? "bg-[rgb(185_138_58/0.12)] text-brass"
        : "bg-rag-unset-bg text-ink-3"

  // Long-form month label for the lock tooltip. Cheap (no allocation per
  // mount because the array is module-level... well, defined here, since
  // module top-level uses SHORT_MONTHS_UC). Inline-tiny string array.
  const monthLong = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ][selectedMonth - 1]
  const monthYearForTooltip = selectedMonth >= 4 ? reportingYear : reportingYear + 1

  return (
    <>
      <Link
        to={`/person/${person.id}`}
        className={[
          "flex items-center gap-[10px] border-t border-rule bg-card px-[18px] py-[13px] text-[13px] font-medium text-ink transition hover:bg-paper",
          isSelf ? "shadow-[inset_2px_0_0_0_var(--color-brass)]" : "",
        ].join(" ")}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[6px] truncate">
            <span className="truncate">{person.fullName}</span>
            {isSelf ? (
              <span className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.08em] text-brass">
                you
              </span>
            ) : null}
            {isLocked ? (
              <span
                className="shrink-0 inline-flex items-center text-brass"
                title={`${person.fullName} has locked ${monthLong} ${monthYearForTooltip}`}
                aria-label={`${monthLong} ${monthYearForTooltip} is locked for ${person.fullName}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </span>
            ) : null}
          </div>
          {person.jobTitle ? (
            <div className="truncate font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
              {person.jobTitle}
            </div>
          ) : null}
        </div>
        {person.team ? (
          <span
            className={`shrink-0 rounded-full px-[7px] py-[1px] text-[10px] font-medium ${teamPill}`}
          >
            {person.team}
          </span>
        ) : null}
      </Link>
      {categoryNames.map((cat) => {
        const hasObj = personObjs?.has(cat) ?? false
        const rag: Rag = hasObj ? getRag(person.fullName, cat) : null
        return (
          <Link
            key={cat}
            to={`/person/${person.id}`}
            title={`${person.fullName} · ${cat} · ${
              hasObj ? ragLabel(rag) : "No objective recorded"
            }`}
            className={[
              "grid border-t border-l border-rule bg-card p-2 transition hover:bg-paper",
              isSelf ? "shadow-[inset_0_2px_0_0_var(--color-brass)]" : "",
            ].join(" ")}
          >
            <Swatch rag={rag} hasObj={hasObj} />
          </Link>
        )
      })}
      <Link
        to={`/person/${person.id}`}
        title={risk ? `${person.fullName} · ${risk.label}` : `${person.fullName} · no objectives`}
        className={[
          "flex items-center justify-center border-t border-l border-rule bg-card p-2 transition hover:bg-paper",
          isSelf ? "shadow-[inset_0_2px_0_0_var(--color-brass)]" : "",
        ].join(" ")}
      >
        <RiskPill risk={risk} />
      </Link>
    </>
  )
}

/* ---------- swatch ---------- */

function Swatch({ rag, hasObj }: { rag: Rag; hasObj: boolean }) {
  if (!hasObj) {
    return (
      <div className="h-[28px] w-full rounded-md border border-dashed border-rule-strong opacity-60" />
    )
  }
  let bg = "bg-rag-unset opacity-40"
  if (rag === "G") bg = "bg-rag-green"
  else if (rag === "A") bg = "bg-rag-amber"
  else if (rag === "R") bg = "bg-rag-red"
  return <div className={`h-[28px] w-full rounded-md ${bg}`} />
}

/* ---------- RAG filter chip ---------- */

function RagFilterChip({
  label,
  checked,
  swatch,
  onChange,
}: {
  label: string
  checked: boolean
  swatch: string
  onChange: (v: boolean) => void
}) {
  return (
    <label
      className={[
        "inline-flex cursor-pointer items-center gap-[8px] rounded-sm border bg-card px-[10px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
        checked
          ? "border-rule-strong text-ink-2 hover:text-ink"
          : "border-rule text-ink-3 opacity-70 hover:opacity-100",
      ].join(" ")}
      title={
        checked
          ? `Hide people whose scored cells are all ${label}`
          : `Show people whose scored cells are all ${label}`
      }
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-[14px] w-[14px] accent-nymr-navy"
      />
      <span className={`inline-block h-[10px] w-[10px] rounded-sm ${swatch}`} />
      {label}
    </label>
  )
}
